// Receipts tab data + actions (slice 4e). Mirrors
// `useManufacturingInventoryChangelog` for the load side and folds in
// an upload + delete pair so the Receipts tab can be a self-contained
// surface (list + Upload receipt button + per-row delete) without the
// parent panel having to hold any of this state.
import { useCallback, useEffect, useState } from "react";
import {
  deleteManufacturingReceipt,
  listManufacturingReceipts,
  uploadManufacturingReceipt,
  type ReceiptUploadMetadata,
  type ReceiptUploadResult,
} from "@/lib/manufacturingClient";
import type {
  MfgInventoryReceipt,
  MfgInventoryReceiptsListFilters,
} from "@/lib/manufacturing/types";

export type UseManufacturingReceiptsResult = {
  receipts: MfgInventoryReceipt[];
  total: number;
  loading: boolean;
  working: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  upload: (args: {
    file: File;
    metadata: ReceiptUploadMetadata;
  }) => Promise<ReceiptUploadResult>;
  remove: (receiptId: string) => Promise<{ ok: true } | { ok: false; error: string }>;
};

export function useManufacturingReceipts(opts: {
  enabled: boolean;
  filters?: MfgInventoryReceiptsListFilters;
}): UseManufacturingReceiptsResult {
  const { enabled } = opts;
  const filters = opts.filters;
  const [receipts, setReceipts] = useState<MfgInventoryReceipt[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setWorking(true);
    setError(null);
    const r = await listManufacturingReceipts(filters ?? {});
    setWorking(false);
    if (!r.ok) {
      setError(r.error);
      setReceipts([]);
      setTotal(0);
      return;
    }
    setReceipts(r.data.receipts);
    setTotal(r.data.total);
  }, [enabled, filters]);

  useEffect(() => {
    let cancelled = false;
    if (!enabled) {
      setReceipts([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    void (async () => {
      const r = await listManufacturingReceipts(filters ?? {});
      if (cancelled) return;
      setLoading(false);
      if (!r.ok) {
        setError(r.error);
        setReceipts([]);
        setTotal(0);
        return;
      }
      setReceipts(r.data.receipts);
      setTotal(r.data.total);
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, filters]);

  const upload = useCallback(
    async (args: { file: File; metadata: ReceiptUploadMetadata }) => {
      setWorking(true);
      setError(null);
      const r = await uploadManufacturingReceipt(args);
      setWorking(false);
      if (!r.ok) {
        setError(r.error);
        return r;
      }
      await refresh();
      return r;
    },
    [refresh]
  );

  const remove = useCallback(
    async (receiptId: string) => {
      setWorking(true);
      setError(null);
      const r = await deleteManufacturingReceipt(receiptId);
      setWorking(false);
      if (!r.ok) {
        setError(r.error);
        return { ok: false as const, error: r.error };
      }
      await refresh();
      return { ok: true as const };
    },
    [refresh]
  );

  return { receipts, total, loading, working, error, refresh, upload, remove };
}
